import { lazy, Suspense, useEffect } from 'react';
import ProjetosProvider from 'contexts/projetos/ProjetosContext';
import { matchPath, Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { PrivateRoute } from '../pages/PrivateRoute';
import AutoCadastro from '../pages/AutoCadastro/AutoCadastro';
import { ContratoEdit, Template, Templates } from '../pages/Contratos';
import { Ideb } from '../pages/Escola';
import FluxoProjetos from '../pages/FluxoProjetos/FluxoProjetos';
import Main from '../pages/Main/Main';
import { PrimeiroAcesso } from '../pages/PrimeiroAcesso';
import { Error404 } from '../pages/Errors';
import { ProjectEdit } from '../pages/Projeto';
import {
  CONTRATO_VERSAO_ADITIVO_NAME,
  CONTRATO_VERSAO_DISTRATO_NAME,
} from './ContratoVersoes';
import { EscolaSteps } from 'pages/Escola/Cadastro/etapas/ProgramasEducacionais/components/EscolaSteps';
import LoaderGlobal from 'components/Loader/LoaderApp/LoaderGlobal';
import { EscolaEditTabs } from 'pages/Escola/Cadastro/etapas/ProgramasEducacionais/__enum';
import { UnauthorizedScreen } from 'components/UnauthorizedScreen';
import { AuthCallback } from 'services/Auth/AuthService';
import userManager from 'services/Auth/oidc.config';

export const basePath = '/programaseducacionais/admin';
const routes = [];

export const getRedirectElement = (path) => {
  return <Route render={() => <Redirect to={path} />} />;
};

const DashboardLazy = lazy(() => import('../pages/Dashboard/Dashboard'));
export const dashboardRoute = {
  path: basePath + '/dashboard',
  title: "Dashboard",
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <DashboardLazy />
    </Suspense>
  ),
  exact: true,
};
routes.push(dashboardRoute);

export const primeiroAcessoRoute = {
  path: basePath + '/primeiro-acesso',
  component: PrimeiroAcesso,
  exact: true,
  logoutAccess: true,
  title: "Primeiro acesso",
};
routes.push(primeiroAcessoRoute);

const RelatoriosPedagogicosLazy = lazy(() =>
  import('../pages/Relatorios/Relatorios')
);
const relatoriosPath = basePath + '/relatorios';

export const relatoriosRoute = {
  path: relatoriosPath,
  title: "Relatórios",
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <RelatoriosPedagogicosLazy />
    </Suspense>
  ),
  exact: true,
};
routes.push(relatoriosRoute);

export const unauthorized = {
  path: '/unauthorized',
  component: UnauthorizedScreen,
  exact: true,
};
routes.push(unauthorized);

export const autoCadastroRoute = {
  path: basePath + '/cadastro',
  component: AutoCadastro,
  exact: true,
  logoutAccess: true,
  title: "Auto cadastro",
};
routes.push(autoCadastroRoute);

const AgendaAssessoresPedagogicosLazy = lazy(() =>
  import('../pages/AgendaAssessoresPedagogicos/AgendaAssessoresPedagogicos')
);
export const agendaAssessoresPedagogicosRoute = {
  path: basePath + '/agenda-assessores-pedagogicos',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <AgendaAssessoresPedagogicosLazy />
    </Suspense>
  ),
  exact: true,
  title: "Agenda de assessores pedagógicos",
};
routes.push(agendaAssessoresPedagogicosRoute);

export const projetosPath = basePath + '/projetos/pufv';
const ProjetosLazy = lazy(() => import('../pages/Projeto/Projetos/Projetos'));
export const projetosRoute = {
  path: projetosPath,
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <ProjetosLazy />
    </Suspense>
  ),
  exact: true,
  title: "Projetos",
};
routes.push(projetosRoute);

// PROJETO NOVO
export const baseProjectPath = `${basePath}/projeto/pufv/passo/:stepSlugName`;

// PROJETO NOVO COM ID
export const baseProjectPathWithId = `${basePath}/projeto/pufv/:id/passo/:stepSlugName`;

// PROJETO ANTIGO COM ID
const oldProjectWithId = `${basePath}/projeto-legado/pufv/:id/passo/:stepSlugName`;

export const projectFluxControlRoute = {
  path: [baseProjectPathWithId, baseProjectPath],
  component: () => (
    <ProjetosProvider>
      <FluxoProjetos />
    </ProjetosProvider>
  ),
  title: "Projeto legado",
  exact: true,
  getURLWithParams: (obj) => {
    return baseProjectPathWithId
      .replace('/:id', obj.id ? `/${obj.id}` : '')
      .replace('/:stepSlugName', `/${obj.step || '/'}`);
  },
};
routes.push(projectFluxControlRoute);

export const projetosWithPreSearchPath =
  basePath + '/projetos/pufv/:tipo?/:search?';
export const projetosWithPreSearchRoute = {
  path: projetosWithPreSearchPath,
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <ProjetosLazy />
    </Suspense>
  ),
  exact: true,
  title: "Projetos",
};
routes.push(projetosWithPreSearchRoute);

// FLUXO DO PROJETO ANTIGO (BLOQUEADO )
export const projectEdit = {
  path: oldProjectWithId,
  component: ProjectEdit,
  title: "Projetos",
  getURLWithParams: (id, stepSlugName) => {
    if (!id) {
      throw Error('Você precisa passar o "id" do projeto.');
    }
    if (!stepSlugName) {
      throw Error('Você precisa passar o "stepSlugName".');
    }
    return oldProjectWithId
      .split(':id')
      .join(id)
      .split(':stepSlugName')
      .join(stepSlugName);
  },
};
routes.push(projectEdit);

const EmpresasLazy = lazy(() => import('../pages/Empresa/Empresas/Empresas'));
export const empresasRoute = {
  path: basePath + '/empresas',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <EmpresasLazy />
    </Suspense>
  ),
  exact: true,
  title: "Empresas",
};
routes.push(empresasRoute);

const ProfessoresLazy = lazy(() => import('../pages/Professor/Professores'));
export const professoresRoute = {
  path: basePath + '/professores',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <ProfessoresLazy />
    </Suspense>
  ),
  exact: true,
  title: "Professores",
};
routes.push(professoresRoute);

const AssessoresLazy = lazy(() => import('../pages/Assessor/Assessores'));
export const assessoresRoute = {
  path: basePath + '/assessores',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <AssessoresLazy />
    </Suspense>
  ),
  exact: true,
  title: "Assessores",
};
routes.push(assessoresRoute);

const CordenadoresLazy = lazy(() =>
  import('../pages/Coordenador/Coordenadores')
);
export const coordenadoresRoute = {
  path: basePath + '/coordenadores',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <CordenadoresLazy />
    </Suspense>
  ),
  exact: true,
  title: "Coordenadores",
};
routes.push(coordenadoresRoute);

const ContratosLazy = lazy(() =>
  import('../pages/Contratos/Fluxos/Contratos')
);
const contratosPath = basePath + '/contratos';
export const contratosRoute = {
  path: contratosPath,
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <ContratosLazy />
    </Suspense>
  ),
  exact: true,
  title: "Contratos",
};
routes.push(contratosRoute);

const contratoPath = basePath + '/contrato/:tipoContrato';
const contratoDescentralizadoPath = basePath + '/contrato/:tipoContrato';
const contratoNovoPath = contratoPath + '/:etapa';

const contratoDescentralizadoNovoPath = contratoDescentralizadoPath + '/:etapa';
export const contratoDescentralizadoRoute = {
  path: contratoDescentralizadoNovoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    return contratoDescentralizadoNovoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoDescentralizadoRoute);

const contratoAditivoNovoPath =
  contratoPath + '/:idContrato/' + CONTRATO_VERSAO_ADITIVO_NAME + '/:etapa';
export const contratoAditivoNovoRoute = {
  path: contratoAditivoNovoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um aditivo.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoAditivoNovoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoAditivoNovoRoute);

const contratoAditivoNovoPathDescentralizado =
  contratoDescentralizadoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_ADITIVO_NAME +
  '/:etapa';
export const contratoAditivoNovoRouteDescentralizado = {
  path: contratoAditivoNovoPathDescentralizado,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um aditivo.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoAditivoNovoPathDescentralizado
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoAditivoNovoRouteDescentralizado);

const contratoDistratoNovoPath =
  contratoPath + '/:idContrato/' + CONTRATO_VERSAO_DISTRATO_NAME + '/:etapa';
export const contratoDistratoNovoRoute = {
  path: contratoDistratoNovoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um distrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do distrato.'
      );
    }
    return contratoDistratoNovoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoDistratoNovoRoute);

const contratoDistratoNovoPathDescentralizado =
  contratoDescentralizadoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_DISTRATO_NAME +
  '/:etapa';
export const contratoDistratoNovoRouteDescentralizado = {
  path: contratoDistratoNovoPathDescentralizado,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um distrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do distrato.'
      );
    }
    return contratoDistratoNovoPathDescentralizado
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoDistratoNovoRouteDescentralizado);

const contratoEditPath = contratoPath + '/:idContrato/:etapa';
export const contratoEditRoute = {
  path: contratoEditPath,
  component: ContratoEdit,
  title: "Contratos",
  exact: true,
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um contrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    return contratoEditPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoEditRoute);

const contratoEditPathDescentralizado =
  contratoDescentralizadoPath + '/:idContrato/:etapa';
export const contratoEditRouteDescentralizado = {
  path: contratoEditPathDescentralizado,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um contrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    return contratoEditPathDescentralizado
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoEditRouteDescentralizado);

const contratoAditivoEditPath =
  contratoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_ADITIVO_NAME +
  '/:idAditivo/:etapa';
export const contratoAditivoEditRoute = {
  path: contratoAditivoEditPath,
  component: ContratoEdit,
  title: "Contratos",
  exact: true,
  getURLWithParams: (tipoContrato, idContrato, idAditivo, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um aditivo.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!idAditivo) {
      throw Error('Você precisa passar o "idAditivo" do aditivo.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoAditivoEditPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':idAditivo')
      .join(idAditivo)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoAditivoEditRoute);

const contratoAditivoEditPathDescentralizado =
  contratoDescentralizadoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_ADITIVO_NAME +
  '/:idAditivo/:etapa';
export const contratoAditivoEditRouteDescentralizado = {
  path: contratoAditivoEditPathDescentralizado,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, idAditivo, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um aditivo.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!idAditivo) {
      throw Error('Você precisa passar o "idAditivo" do aditivo.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoAditivoEditPathDescentralizado
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':idAditivo')
      .join(idAditivo)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoAditivoEditRouteDescentralizado);

const contratoDistratoEditPath =
  contratoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_DISTRATO_NAME +
  '/:idDistrato/:etapa';
export const contratoDistratoEditRoute = {
  path: contratoDistratoEditPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, idDistrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um distrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!idDistrato) {
      throw Error('Você precisa passar o "idDistrato" do distrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoDistratoEditPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':idDistrato')
      .join(idDistrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoDistratoEditRoute);

const contratoDistratoEditPathDescentralizado =
  contratoDescentralizadoPath +
  '/:idContrato/' +
  CONTRATO_VERSAO_DISTRATO_NAME +
  '/:idDistrato/:etapa';
export const contratoDistratoEditRouteDescentralizado = {
  path: contratoDistratoEditPathDescentralizado,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, idContrato, idDistrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para editar um distrato.'
      );
    }
    if (!idContrato) {
      throw Error('Você precisa passar o "idContrato" do contrato.');
    }
    if (!idDistrato) {
      throw Error('Você precisa passar o "idDistrato" do distrato.');
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do aditivo.'
      );
    }
    return contratoDistratoEditPathDescentralizado
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':idContrato')
      .join(idContrato)
      .split(':idDistrato')
      .join(idDistrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoDistratoEditRouteDescentralizado);

const templatesContratosPath = contratosPath + '/templates';
export const templatesContratoRoute = {
  path: templatesContratosPath,
  component: Templates,
  exact: true,
  title: "Templates",
};
routes.push(templatesContratoRoute);

const templateContratoEditPath = contratosPath + '/template/:id';
export const templateContratoEditRoute = {
  path: templateContratoEditPath,
  component: Template,
  exact: true,
  title: "Templates",
  getURLWithParams: (id) => {
    if (!id) {
      throw Error('Você precisa passar o "id" do template.');
    }
    return templateContratoEditPath.split(':id').join(id);
  },
};
routes.push(templateContratoEditRoute);

const EscolasLazy = lazy(() => import('../pages/Escola/Escolas/Escolas'));
export const escolasRoute = {
  path: basePath + '/escolas',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <EscolasLazy />
    </Suspense>
  ),
  exact: true,
  title: "Escolas",
};
routes.push(escolasRoute);

const PufvLazy = lazy(() => import('../pages/MateriaisPedagogicos/Pufv/Pufv'));
export const uniaoFazAVidaRoute = {
  path: basePath + '/materiais-pedagogicos/pufv',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <PufvLazy />
    </Suspense>
  ),
  exact: true,
  title: "Materiais pedagógicos",
};
routes.push(uniaoFazAVidaRoute);

const CooperativasEscolaresLazy = lazy(() =>
  import(
    '../pages/MateriaisPedagogicos/CooperativasEscolares/CooperativasEscolares'
  )
);
export const cooperativasEscolares = {
  path: basePath + '/materiais-pedagogicos/cooperativas-escolares',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <CooperativasEscolaresLazy />
    </Suspense>
  ),
  exact: true,
  title: "Materiais pedagógicos"
};
routes.push(cooperativasEscolares);

const FinancasNaMochilaLazy = lazy(() =>
  import(
    '../pages/MateriaisPedagogicos/FinancasNaMochila/FinancasNaMochila'
  )
);

export const financasNaMochila = {
  path: basePath + '/materiais-pedagogicos/financas-na-mochila',
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <FinancasNaMochilaLazy />
    </Suspense>
  ),
  exact: true,
  title: "Materiais pedagógicos"
};
routes.push(financasNaMochila);

const newEscolaPath = basePath + '/escola';

const createEscolaPath = basePath + '/cadastro/escola';
export const newEscolaRoute = {
  path: createEscolaPath,
  component: EscolaSteps,
  exact: true,
  title: "Escolas"
};
routes.push(newEscolaRoute);

const idebPath = newEscolaPath + '/ideb/:id';
export const idebRoute = {
  path: idebPath,
  component: Ideb,
  exact: true,
  title: "Ideb",
  getURLWithParams: (id) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return idebPath.split(':id').join(id);
  },
};
routes.push(idebRoute);

//CADASTRO/EDICAO NOVO PANORAMA -------------------------------------- (WIP)

const newPeriodoPath =
  createEscolaPath + '/:id/dados-da-escola/PUFV/periodo-cadastro';
export const newPeriodoRoute = {
  path: newPeriodoPath,
  component: EscolaSteps,
  exact: true,
  title: "Escolas",
  getURLWithParams: (id) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return newPeriodoPath.split(':id').join(id);
  },
};
routes.push(newPeriodoRoute);

const editPeriodoPath =
  newEscolaPath + '/:id/dados-da-escola/PUFV/periodo-edicao';
const periodoEditPath = editPeriodoPath + '/:periodo/:key?';
export const periodoEditRoute = {
  path: periodoEditPath,
  component: EscolaSteps,
  exact: true,
  title: "Periodos",
  getURLWithParams: (id, periodo, key) => {
    if (!id) throw Error('Você precisa passar o "id" da escola.');
    if (!periodo) throw Error('Você precisa passar o "período" que deseja editar.');
    let url = periodoEditPath
      .split(':id')
      .join(id)
      .split(':periodo')
      .join(periodo);
    if (key) {
      url = url.split(':key').join(key);
    } else {
      url = url.replace('/:key?', '');
    }
    return url;
  }
};
routes.push(periodoEditRoute);

const newPeriodoCoopPath =
  createEscolaPath + '/:id/dados-da-escola/cooperativa-escolar/periodo-cadastro';
export const newPeriodoCoopRoute = {
  path: newPeriodoCoopPath,
  component: EscolaSteps,
  exact: true,
  title: "Periodos",
  getURLWithParams: (id) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return newPeriodoCoopPath.split(':id').join(id);
  },
};
routes.push(newPeriodoCoopRoute);

const editPeriodoCoopPath =
  newEscolaPath + '/:id/dados-da-escola/cooperativa-escolar/periodo/:edicao';
const periodoEditCoopPath = editPeriodoCoopPath + '/:periodo/:idPeriodo/:key?';
export const periodoEditCoopRoute = {
  path: periodoEditCoopPath,
  component: EscolaSteps,
  exact: true,
  title: "Escolas",
  getURLWithParams: (id, periodo, idPeriodo, edicao, key) => {
    if (!id) throw Error('Você precisa passar o "id" da escola.');
    if (!periodo) throw Error('Você precisa passar o "período" que deseja editar.');
    if (!idPeriodo) throw Error('Você precisa passar o "id do período".');

    let url = periodoEditCoopPath
      .replace(':id', id)
      .replace(':edicao', edicao)
      .replace(':periodo', periodo)
      .replace(':idPeriodo', idPeriodo);

    if (key) {
      url = url.replace(':key?', key);
    } else {
      url = url.replace('/:key?', '');
    }
    return url;
  }
};
routes.push(periodoEditCoopRoute);

const newPeriodoFinancasNaMochilaPath =
  createEscolaPath + '/:id/dados-da-escola/financas-na-mochila/periodo-cadastro';
export const newPeriodoFinancasRoute = {
  path: newPeriodoFinancasNaMochilaPath,
  component: EscolaSteps,
  exact: true,
  title: "Periodos",
  getURLWithParams: (id) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return newPeriodoFinancasNaMochilaPath.split(':id').join(id);
  },
};
routes.push(newPeriodoFinancasRoute);

const editPeriodoFinancasPath =
  newEscolaPath + '/:id/dados-da-escola/financas-na-mochila/periodo/:edicao';
const periodoEditFinancasPath = editPeriodoFinancasPath + '/:periodo/:idPeriodo/:key?';
export const periodoEditFinancasRoute = {
  path: periodoEditFinancasPath,
  component: EscolaSteps,
  exact: true,
  title: "Periodos",
  getURLWithParams: (id, periodo, idPeriodo, edicao, key) => {
    if (!id) throw Error('Você precisa passar o "id" da escola.');
    if (!periodo) throw Error('Você precisa passar o "período" que deseja editar.');
    if (!idPeriodo) throw Error('Você precisa passar o "id do período".');

    let url = periodoEditCoopPath
      .replace(':id', id)
      .replace(':edicao', edicao)
      .replace(':periodo', periodo)
      .replace(':idPeriodo', idPeriodo);

    if (key) {
      url = url.replace(':key?', key);
    } else {
      url = url.replace('/:key?', '');
    }
    return url;
  }
};
routes.push(periodoEditFinancasRoute);

//FIM CADASTRO/EDICAO NOVO PANORAMA -------------------------------------- (WIP)

const escolaEditPath = newEscolaPath + '/:id/:tab';
export const escolaEditRoute = {
  path: escolaEditPath,
  component: EscolaSteps,
  exact: false,
  title: "Escolas",
  getURLWithParams: (id, tab = EscolaEditTabs.Informacoes) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return escolaEditPath.replace(':id', id).replace(':tab', tab);
  },
};
routes.push(escolaEditRoute);

const createEscola = createEscolaPath + '/:id/:tab';
export const createEscolaRoute = {
  path: createEscola,
  component: EscolaSteps,
  exact: false,
  title: "Escolas",
  getURLWithParams: (id, tab = EscolaEditTabs.Informacoes) => {
    if (!id) {
      throw Error('Você precisa passar o "id" da escola.');
    }
    return createEscola.replace(':id', id).replace(':tab', tab);
  },
};
routes.push(createEscolaRoute);

const ModelosDocumentoLazy = lazy(() =>
  import('../pages/Contratos/ModeloDocumento/index')
);
const modelosDocumentosContratosPath = contratosPath + '/modelos-de-documentos';
export const modelosDocumentosContratoRoute = {
  path: modelosDocumentosContratosPath,
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <ModelosDocumentoLazy />
    </Suspense>
  ),
  exact: true,
  title: "Modelos de documentos",
};
routes.push(modelosDocumentosContratoRoute);

const CadastroDocumentosLazy = lazy(() =>
  import('../pages/Contratos/UploadDocumento/index')
);
const cadastroDocumentosContratosPath = contratosPath + '/cadastro-de-documentos';
export const cadastroDocumentosContratoRoute = {
  path: cadastroDocumentosContratosPath,
  component: () => (
    <Suspense fallback={<LoaderGlobal />}>
      <CadastroDocumentosLazy />
    </Suspense>
  ),
  exact: true,
  title: "Cadastro de documentos",
};
routes.push(cadastroDocumentosContratoRoute);

//NEW PATH FLUXO
const contratoDescentralizadoFluxoPath = basePath + '/contrato/:tipoContrato';

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - CREATE MODAL ADITIVO:
const createAditivoDescentralizadoPath =
  contratoDescentralizadoFluxoPath + '/:etapa/:id/cadastro-aditivo/:passo/:criaAditivoModal';

export const createAditivoDescentralizadoRoute = {
  path: createAditivoDescentralizadoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, etapa, id, passo, criaAditivoModal) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    if (!id) {
      throw Error('Você precisa passar o "id" da criação/edição do contrato.');
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    return createAditivoDescentralizadoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa)
      .split(':id')
      .join(id)
      .split(':passo')
      .join(passo)
      .split(':criaAditivoModal')
      .join(criaAditivoModal);
  },
};
routes.push(createAditivoDescentralizadoRoute);

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - CREATE MODAL DISTRATO:
const createDistratoDescentralizadoPath =
  contratoDescentralizadoFluxoPath + '/:etapa/:id/cadastro-distrato/:passo/:criaDistratoModal';
export const createDistratoDescentralizadoRoute = {
  path: createDistratoDescentralizadoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",

  getURLWithParams: (tipoContrato, etapa, id, passo, criaDistratoModal) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    if (!id) {
      throw Error('Você precisa passar o "id" da criação/edição do contrato.');
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    return createDistratoDescentralizadoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa)
      .split(':id')
      .join(id)
      .split(':passo')
      .join(passo)
      .split(':criaDistratoModal')
      .join(criaDistratoModal);
  },
};
routes.push(createDistratoDescentralizadoRoute);

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - CREATE:
const newContratoDescentralizadoPath =
  contratoDescentralizadoFluxoPath + '/cadastro-de-contratos/:passo';
export const newContratoDescentralizadoRoute = {
  path: newContratoDescentralizadoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",

  getURLWithParams: (tipoContrato, passo) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    return newContratoDescentralizadoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':passo')
      .join(passo);
  },
};
routes.push(newContratoDescentralizadoRoute);

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - EDIT:
const newContratoDescentralizadoEditPath =
  contratoDescentralizadoFluxoPath + '/:etapa/:id/:passo/:param?';

export const newContratoDescentralizadoEditRoute = {
  path: newContratoDescentralizadoEditPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",

  getURLWithParams: (tipoContrato, etapa, id, passo, notificacaoAditivo) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    if (!id) {
      throw Error('Você precisa passar o "id" da criação/edição do contrato.');
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    let url = newContratoDescentralizadoEditPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa)
      .split(':id')
      .join(id)
      .split(':passo')
      .join(passo);
    if (notificacaoAditivo) {
      url = url.split(':param').join(notificacaoAditivo);
    } else {
      url = url.replace('/:param?', '');
    }
    return url;
  },
};
routes.push(newContratoDescentralizadoEditRoute);

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - EDIT MODAL ADITIVO:
const editAditivoDescentralizadoPath =
  contratoDescentralizadoFluxoPath +
  '/:etapa/:id/edicao-aditivo/:idAditivo/:versao/:passo/:param?';
export const editAditivoDescentralizadoRoute = {
  path: editAditivoDescentralizadoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",

  getURLWithParams: (
    tipoContrato,
    etapa,
    id,
    passo,
    idAditivo,
    versao,
    modalEditAditivo
  ) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" da criação/edição do contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    if (!id) {
      throw Error('Você precisa passar o "id" da criação/edição do contrato.');
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    if (!idAditivo) {
      throw Error(
        'Você precisa passar o "idAditivo" da criação/edição do contrato.'
      );
    }
    if (!versao) {
      throw Error(
        'Você precisa passar o "versao" da criação/edição do contrato.'
      );
    }
    let url = editAditivoDescentralizadoPath
      .split(':idAditivo')
      .join(idAditivo)
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa)
      .split(':id')
      .join(id)
      .split(':versao')
      .join(versao)
      .split(':passo')
      .join(passo);
    if (modalEditAditivo) {
      url = url.split(':param').join(modalEditAditivo);
    } else {
      url = url.replace('/:param?', '');
    }
    return url;
  },
};
routes.push(editAditivoDescentralizadoRoute);

//FLUXO NOVO CONTRATOS DESCENTRALIZADOS - EDIT DISTRATO:
const editDistratoDescentralizadoPath =
  contratoDescentralizadoFluxoPath +
  '/:etapa/:id/edicao-distrato/:idDistrato/:versao/:passo/:param?';
export const editDistratoDescentralizadoRoute = {
  path: editDistratoDescentralizadoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",

  getURLWithParams: (
    tipoContrato,
    etapa,
    id,
    passo,
    idDistrato,
    versao,
    modalEditDistrato
  ) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" da criação/edição do contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    if (!id) {
      throw Error('Você precisa passar o "id" da criação/edição do contrato.');
    }
    if (!passo) {
      throw Error(
        'Você precisa passar o "passo" da criação/edição do contrato.'
      );
    }
    if (!idDistrato) {
      throw Error(
        'Você precisa passar o "idDistrato" da criação/edição do contrato.'
      );
    }
    if (!versao) {
      throw Error(
        'Você precisa passar o "versao" da criação/edição do contrato.'
      );
    }
    let url = editDistratoDescentralizadoPath
      .split(':idDistrato')
      .join(idDistrato)
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa)
      .split(':id')
      .join(id)
      .split(':versao')
      .join(versao)
      .split(':passo')
      .join(passo);
    if (modalEditDistrato) {
      url = url.split(':param').join(modalEditDistrato);
    } else {
      url = url.replace('/:param?', '');
    }
    return url;
  },
};
routes.push(editDistratoDescentralizadoRoute);

export const contratoNovoRoute = {
  path: contratoNovoPath,
  component: ContratoEdit,
  exact: true,
  title: "Contratos",
  getURLWithParams: (tipoContrato, etapa) => {
    if (!tipoContrato) {
      throw Error(
        'Você precisa passar o "tipoContrato" para criar um contrato.'
      );
    }
    if (!etapa) {
      throw Error(
        'Você precisa passar a "etapa" da criação/edição do contrato.'
      );
    }
    return contratoNovoPath
      .split(':tipoContrato')
      .join(tipoContrato)
      .split(':etapa')
      .join(etapa);
  },
};
routes.push(contratoNovoRoute);

export const mainRedirect = {
  path: '',
  component: Main,
  title: "Projetos",
  exact: true,
};
routes.push(mainRedirect);

export const mainRedirect2 = {
  path: basePath + '/',
  component: Main,
  title: "Projetos",
  exact: true,
};
routes.push(mainRedirect2);

export const authCallbackRoute = {
  path: '/auth/login',
  component: AuthCallback,
  exact: true,
  logoutAccess: true,
};
routes.push(authCallbackRoute);

export const error404Route = {
  component: Error404,
};
routes.push(error404Route);

export const SilentRenewCallback = () => {
  useEffect(() => {
    userManager.signinSilentCallback().catch(err => {
      console.error('Erro no silent renew callback', err);
    });
  }, []);
  return <><LoaderGlobal /></>;
}

export const LogoutCallback = () => {
  const user = userManager.getUser();
  const history = useHistory();
  useEffect(() => {
    const run = async () => {
      try {
        await userManager.signoutRedirectCallback();
        await userManager.removeUser();
        await userManager.clearStaleState();
        await userManager.signoutRedirect({
          id_token_hint: user.id_token,
          post_logout_redirect_uri: window.env.auth.logoutUrl,
        });
      } finally {
        history.replace('/auth/login');
      }
    };
    run();
  }, [history, user.id_token]);
  return null;
}

export const silentRenewRoute = {
  path: '/auth/silent-renew',
  component: SilentRenewCallback,
  exact: true,
  logoutAccess: true,
};
routes.push(silentRenewRoute);

export const authLogoutCallbackRoute = {
  path: '/auth/logout-callback',
  component: LogoutCallback,
  exact: true,
  logoutAccess: true,
};
routes.push(authLogoutCallbackRoute);

export function getRouteComponent(route, index) {
  return route.logoutAccess ? (
    <Route
      key={index}
      path={route.path || null}
      render={route.render ? () => route.render() : null}
      component={route.component}
      exact={route.exact ? true : false}
    />
  ) : (
    <PrivateRoute
      key={index}
      path={route.path || null}
      render={route.render ? () => route.render() : null}
      component={route.component}
      exact={route.exact ? true : false}
    />
  );
}

export function getAllRouteComponents() {
  return routes.map((route, index) => getRouteComponent(route, index));
}

export function getRouteTitle(pathname) {
  for (const route of routes) {
    if (!route.path) continue;

    const paths = Array.isArray(route.path) ? route.path : [route.path];

    for (const path of paths) {
      const match = matchPath(pathname, {
        path,
        exact: route.exact ?? false,
        strict: false,
      });

      if (match) {
        if (!route.title) return '';

        return typeof route.title === 'function'
          ? route.title(match.params)
          : route.title;
      }
    }
  }

  return '';
}

export function useRouteTitle() {
  const location = useLocation();
  return getRouteTitle(location.pathname);
}

